import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Save, Lock, RefreshCw, Copy, Check } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { apiService } from '../../services/apiService';
import { validateEmail, validatePhone, generatePassword } from '../../utils/helpers';
import { useToast } from '../../contexts/ToastContext';

const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currentPassword, setCurrentPassword] = useState(''); // Existing password from DB
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    newPassword: '', // Only for setting a NEW password
  });

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    try {
      setLoadingData(true);
      const data = await apiService.getClient(id);
      setFormData({
        name: data.name || '',
        company: data.company || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        postalCode: data.postalCode || data.postal_code || '',
        newPassword: '',
      });
      // Set current password if available
      setCurrentPassword(data.plainPassword || data.plain_password || '');
    } catch (error) {
      console.error('Failed to load client:', error);
      showError('Erreur lors du chargement du client');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validatePassword = (password) => {
    if (!password) return { valid: true, errors: [] }; // Optional field
    
    const errors = [];
    if (password.length < 8) {
      errors.push('Minimum 8 caractères');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Au moins un chiffre');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Au moins un caractère spécial');
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Le nom est requis';
    if (!formData.company) newErrors.company = "Le nom de l'entreprise est requis";
    if (!formData.email) {
      newErrors.email = "L'email est requis";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = "Le numéro de téléphone n'est pas valide";
    }

    // Validate password if provided
    if (formData.newPassword) {
      const passwordValidation = validatePassword(formData.newPassword);
      if (!passwordValidation.valid) {
        newErrors.newPassword = passwordValidation.errors.join('. ');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const applyPasswordChange = async (password) => {
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setErrors({ newPassword: passwordValidation.errors.join('. ') });
      return false;
    }

    await apiService.resetClientPassword(id, password);
    setCurrentPassword(password);
    setFormData((prev) => ({ ...prev, newPassword: '' }));
    showSuccess('Mot de passe enregistré dans la base de données');
    return true;
  };

  const handlePasswordSave = async () => {
    if (!formData.newPassword?.trim()) {
      showError('Saisissez ou générez un nouveau mot de passe');
      return;
    }

    try {
      setLoading(true);
      await applyPasswordChange(formData.newPassword.trim());
    } catch (error) {
      console.error('Failed to reset client password:', error);
      showError(error.message || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);

      const dataToSend = {
        name: formData.name,
        company: formData.company,
        phone: formData.phone || null,
        address: formData.address || null,
        city: formData.city || null,
        postal_code: formData.postalCode || null,
      };

      if (formData.newPassword && formData.newPassword.trim() !== '') {
        await applyPasswordChange(formData.newPassword.trim());
      }

      await apiService.updateClient(id, dataToSend);
      showSuccess('Client mis à jour avec succès');
      navigate('/clients');
    } catch (error) {
      console.error('Failed to update client:', error);
      showError(error.message || 'Erreur lors de la mise à jour du client');
      setErrors({ submit: error.message || 'Erreur lors de la mise à jour du client' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate('/clients')}
          className="mb-4"
        >
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Modifier le Client</h1>
        <p className="text-gray-600 mt-1">Mettez à jour les informations du client</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informations personnelles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom complet"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              required
            />
            <Input
              label="Entreprise"
              name="company"
              value={formData.company}
              onChange={handleChange}
              error={errors.company}
              required
            />
            <Input
              label="Email (connexion)"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
              disabled
            />
            <Input
              label="Téléphone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
            />
          </div>
        </Card>

        {/* Password Section */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Mot de passe du client
          </h2>
          
          <div className="space-y-4">
            {/* Current Password Display */}
            {currentPassword && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Mot de passe actuel (usage interne admin — ne pas partager par email non sécurisé) :
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-xl font-bold text-blue-900 bg-white px-4 py-2 rounded border border-blue-200 select-all flex-1">
                    {currentPassword}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    icon={Copy}
                    onClick={() => {
                      navigator.clipboard.writeText(currentPassword);
                      showSuccess('Mot de passe copié !');
                    }}
                  >
                    Copier
                  </Button>
                </div>
              </div>
            )}

            {!currentPassword && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ Aucun mot de passe enregistré. Générez-en un nouveau ci-dessous.
                </p>
              </div>
            )}

            {/* Generate New Password Button */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                type="button"
                variant="outline"
                icon={RefreshCw}
                onClick={() => {
                  const newPass = generatePassword();
                  setFormData({ ...formData, newPassword: newPass });
                  setShowNewPassword(true);
                  setCopied(false);
                }}
              >
                {currentPassword ? 'Changer le mot de passe' : 'Générer un mot de passe'}
              </Button>
              
              {formData.newPassword && (
                <>
                  <Button
                    type="button"
                    variant={copied ? 'primary' : 'outline'}
                    icon={copied ? Check : Copy}
                    onClick={() => {
                      navigator.clipboard.writeText(formData.newPassword);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                  >
                    {copied ? 'Copié !' : 'Copier nouveau'}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    icon={Save}
                    loading={loading}
                    onClick={handlePasswordSave}
                  >
                    Enregistrer le mot de passe
                  </Button>
                </>
              )}
            </div>

            {/* New Password Input */}
            {(formData.newPassword || !currentPassword) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentPassword ? 'Nouveau mot de passe' : 'Mot de passe'}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={(e) => {
                      handleChange(e);
                      setCopied(false);
                    }}
                    placeholder="Cliquez sur 'Générer' ou saisissez manuellement"
                    className={`flex-1 px-3 py-2 border rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    } ${formData.newPassword ? 'bg-green-50 border-green-300 font-mono text-lg' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title={showNewPassword ? 'Masquer' : 'Afficher'}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-xs text-red-600 mt-1">{errors.newPassword}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Le mot de passe doit contenir au moins 8 caractères, un chiffre et un caractère spécial.
                </p>
              </div>
            )}

            {/* New Password Preview Box */}
            {formData.newPassword && showNewPassword && (
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                <p className="text-sm font-medium text-green-800 mb-2">
                  ✅ Nouveau mot de passe à communiquer au client :
                </p>
                <p className="font-mono text-xl font-bold text-green-900 bg-white px-4 py-2 rounded border border-green-200 select-all">
                  {formData.newPassword}
                </p>
                <p className="text-xs text-green-700 mt-2">
                  ⚠️ Enregistrez les modifications pour appliquer ce mot de passe !
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Adresse</h2>
          <div className="space-y-4">
            <Input
              label="Adresse"
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={errors.address}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Ville"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
              <Input
                label="Code postal"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
              />
            </div>
          </div>
        </Card>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/clients')}
          >
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={loading} icon={Save}>
            Enregistrer les modifications
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditClient;
